/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import sass from "./drop.module.sass";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";

type Position = "left" | "center" | "right";

interface IDrop extends PropsWithChildren {
	target: HTMLElement | null;
	position?: Position;
	widthTarget?: boolean;
	zIndex?: number;
	maxHeight?: number;
	outsideClick?: () => void;
}

/**
 * @param {IDrop} props
 * @param {any} props.target - ссылка на элемент под которым нужно показывать DropDown
 * @param {boolean} props.widthTarget - булевое значение, использовать ли ширину цели для отрисовки DropDown, по умолчанию false
 * @param {('left'|'center'|'right')} props.position - 'left'|'center'|'right' где относительно низа показывать DropDown
 * @param {any} props.children - то что должно рендерится внутри DropDown
 * @param {number} props.zIndex - css zIndex
 * @constructor
 */
const DropSelect: React.FC<IDrop> = ({
	outsideClick = () => {},
	target,
	maxHeight,
	widthTarget = false,
	position = "right",
	children,
	zIndex = 1,
}) => {
	const drop = useRef<HTMLDivElement>(null);

	/**
	 * локальный стэйт для позиционирования DropDown
	 */
	const [cord, setCord] = useState<any>({});

	/**
	 * функция калькулятор - расчитывает координаты положения DropDown относительно цели
	 */
	function calculate() {
		if (target && drop.current) {
			const { bottom, width, left, right } = target.getBoundingClientRect();
			const widthDrop = drop.current?.getBoundingClientRect().width;
			let chord: { [key: string]: any };
			switch (position) {
				case "right":
					chord = {
						top: bottom + 5,
						left: right - widthDrop!,
						...(widthTarget && { width }),
					};
					break;
				case "left":
					chord = {
						top: bottom + 5,
						left: left,
						...(widthTarget && { width }),
					};
					break;
				case "center":
					chord = {
						top: bottom + 5,
						left: left + width / 2,
						transform: "translateX(-50%)",
						...(widthTarget && { width }),
					};
					break;
				default:
					chord = {};
			}
			if (
				target.getBoundingClientRect().bottom + drop.current?.getBoundingClientRect().height! >=
				window.document.body.getBoundingClientRect().bottom
			) {
				const { top, ...restChord } = chord;
				let newChord = {
					...restChord,
					top:
						target?.getBoundingClientRect().bottom! -
						drop.current?.getBoundingClientRect().height! -
						target.getBoundingClientRect().height,
				};
				setCord(newChord);
			} else {
				setCord(chord);
			}
		}
	}

	/**
	 * при изменении позиции или цели запускается перерасчет координат
	 */
	useEffect(() => {
		calculate();
		//eslint-disable-next-line
	}, [target, position]);

	/**
	 * эффект следит за изменением окна браузера и запускает перерасчет позиции DropDown
	 */
	useEffect(() => {
		window.addEventListener("resize", () => calculate());
		document.addEventListener("scroll", () => calculate(), true);
		return () => {
			window.removeEventListener("resize", () => calculate());
			document.removeEventListener("scroll", () => calculate(), true);
		};
		//eslint-disable-next-line
	}, []);

	useOutsideAlerter(drop.current, outsideClick);

	return createPortal(
		<div ref={drop} className={sass.dropdown} style={{ ...cord, zIndex, ...(maxHeight && { maxHeight }) }}>
			{children}
		</div>,
		document.body,
	);
};

export default DropSelect;
